import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { IConfirmation } from 'core/types';
import { QuoteEntityType, QuoteNoteType, QuoteType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { mapQuoteNoteToForm, mapUpsertQuoteNoteForm } from 'quotes/mappers';
import { ICustomerQuote, IQuoteNote, IQuoteNoteForm } from 'quotes/types';
import { getQuoteNoteContent } from 'quotes/utils';
import { Color } from 'theme/enums';

import { QuoteNoteForm } from './QuoteNoteForm';
import { quoteNoteSchema } from './schema';

interface IProps {
  quote: ICustomerQuote;
  setQuote?: (quote: ICustomerQuote) => void;
  type: QuoteNoteType;
  entityType?: QuoteEntityType;
  isEditable?: boolean;
  quoteType?: QuoteType;
}

export const QuoteNote = ({
  quote,
  setQuote,
  type,
  entityType = QuoteEntityType.QUOTE,
  isEditable = true,
  quoteType = QuoteType.CUSTOMER,
}: IProps) => {
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
  const [confirmationOption, setConfirmationOption] = useState<IConfirmation>(null);

  const { t } = useTranslation();
  const { clearNotificationTimeout } = useNotificationContext();
  const { upsertQuoteNote } = useQuoteActions({
    notificationPosition: NotificationPosition.DRAWER,
    entityType,
  });

  const {
    control,
    getValues,
    handleSubmit,
    watch,
    formState: { dirtyFields },
  } = useForm<IQuoteNoteForm>({
    resolver: yupResolver(quoteNoteSchema()),
    defaultValues: mapQuoteNoteToForm(quote, type),
  });

  const defaultNoteContent = getQuoteNoteContent(quote, type);
  const watchContent = watch('content', defaultNoteContent);

  useEffect(() => {
    if (!dirtyFields.content) {
      return;
    }

    if (isEditable && !submitEnabled) {
      setSubmitEnabled(true);

      return;
    }
  }, [dirtyFields.content, isEditable, submitEnabled, watchContent]);

  const onSubmit = async (data: IQuoteNoteForm) => {
    if (!isEditable) {
      return;
    }

    if (QuoteNoteType.CUSTOMER_NOTE === type) {
      setConfirmationOption({
        title: t('titleWarning'),
        message: t('warningQuoteCustomerNoteSave'),
        confirmBtnColor: Color.SUCCESS,
        onConfirm: () => {
          setConfirmationOption(null);

          saveNote(data);
        },
        onCancel: () => setConfirmationOption(null),
      });

      return;
    }

    saveNote(data);
  };

  const saveNote = async (data: IQuoteNoteForm) => {
    const { success, quoteNote } = await upsertQuoteNote(mapUpsertQuoteNoteForm(data, quote, type));

    if (!success) {
      return;
    }

    setQuote && setQuote(getUpdatedQuote(quoteNote));

    setSubmitEnabled(false);

    clearNotificationTimeout();
  };

  const getUpdatedQuote = (quoteNote: IQuoteNote) => {
    switch (type) {
      case QuoteNoteType.INTERNAL_NOTE:
        return {
          ...quote,
          notesMap: {
            ...quote.notesMap,
            [QuoteNoteType.INTERNAL_NOTE]: quoteNote,
          },
        };
      case QuoteNoteType.CUSTOMER_NOTE:
        return {
          ...quote,
          notesMap: {
            ...quote.notesMap,
            [QuoteNoteType.CUSTOMER_NOTE]: quoteNote,
          },
        };
    }
  };

  return (
    <QuoteNoteForm
      control={control}
      getValues={getValues}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      type={type}
      submitEnabled={submitEnabled}
      confirmationOption={confirmationOption}
      disabledFields={!isEditable}
      entityType={entityType}
      isAlternateEnabled={Boolean(quote.alternateVersionEnabled)}
      quoteType={quoteType}
    />
  );
};
